import { omit } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { push as navigateTo } from 'connected-react-router';
import { useDispatch } from 'react-redux';
import { Permission } from 'shared/permissions';
import { toError } from 'shared/util/toError';
import { BuildingId, BuildingLinkCode, BuildingOrg, Contract, ContractId, OrgId } from 'shared/models';
import { useContracts, useOrgs, useSession } from 'hooks';
import { callApi } from 'apiClient';
import { Dialog } from 'ui/shared/containers';
import { BuildingOrgForm, FormData } from './BuildingOrgForm';

export type Props = {
    buildingId: BuildingId;
    visible: boolean;
    orgId: OrgId | null;
    buildingOrgs?: Record<OrgId, BuildingOrg>;
    contracts: Record<ContractId, Contract>;
    hasEcs: boolean;
    busy: boolean;
    onBuildingOrgsChanged(bo: Record<OrgId, BuildingOrg>): void;
    onLinkCodeGenerated(linkCode: BuildingLinkCode): void;
    onClose(): void;
};

type State = {
    busy: boolean;
    errorKey?: string;
};

export const BuildingOrgDialog = ({ buildingId, visible, orgId: editOrgId, buildingOrgs, hasEcs, onBuildingOrgsChanged, onLinkCodeGenerated, onClose }: Props) => {
    const { hasPermission } = useSession();

    const { orgs } = useOrgs();
    const [ orgId, setOrgId ] = useState<OrgId | null>(editOrgId);
    const { contractIds, contracts, isBusy: isBusyContracts } = useContracts({ orgId });
    const [ state, setState ] = useState<State>({ busy: false });
    
    const dispatch = useDispatch();

    const org = orgs[orgId!] || null;
    const isNew = !editOrgId;
    
    const handleConfigure = useCallback(() => {
        dispatch(navigateTo(`/admin/organizations/${orgId}/settings`));
    }, [ dispatch, orgId ]);

    //
    // Create a link code (for users who don't have permission to see other Orgs)
    //
    const generateBuildingLinkCode = useCallback(({ orgId, contractId, ...formData }: FormData) => {
        setState({ busy: true });
        void (async () => {
            try {
                // Note: contractId is unused here (will be null)
                const buildingLinkCode = await callApi<BuildingLinkCode>('POST', `/api/buildings/${buildingId}/orgs`, formData);
                onLinkCodeGenerated(buildingLinkCode);
                setState({ busy: false });
            }
            catch (e) {
                const err = toError(e);
                setState({
                    busy: false,
                    errorKey: err.message,
                });
            }
        })();
    }, [ buildingId, onLinkCodeGenerated, setState ]);

    //
    // Create an immediate link between a Building and an Org (for AllOrgs users only)
    //
    const linkOrgToBuilding = useCallback((orgId: OrgId, contractId: ContractId | null, properties: BuildingOrg['properties'], replace: BuildingOrg['properties']) => {
        setState({ busy: true });
        void (async () => {
            try {
                const buildingOrgs = await callApi<Record<OrgId, BuildingOrg>>('POST', `/api/buildings/${buildingId}/orgs/${orgId}`, {
                    contractId,
                    properties,
                    replace,
                });
                onBuildingOrgsChanged(buildingOrgs);
                setState({ busy: false });
                onClose();
            }
            catch (e) {
                const err = toError(e);
                setState({
                    busy: false,
                    errorKey: err.message,
                });
            }
        })();
    }, [ buildingId, setState, onBuildingOrgsChanged, onClose ]);

    //
    // Update an existing BuildingOrg link
    //
    const updateBuildingOrg = useCallback(({ buildingId, orgId, ...buildingOrg }: BuildingOrg & { replace: BuildingOrg['properties'] }) => {
        setState({ busy: true });
        void (async () => {
            try {
                const buildingOrgs = await callApi<Record<OrgId, BuildingOrg>>('PUT', `/api/buildings/${buildingId}/orgs/${orgId}`, buildingOrg);
                onBuildingOrgsChanged(buildingOrgs);
                setState({ busy: false });
                onClose();
            }
            catch (e) {
                const err = toError(e);
                setState({
                    busy: false,
                    errorKey: err.message,
                });
            }
        })();
    }, [ setState, onClose, onBuildingOrgsChanged ]);

    const handleSave = useCallback((formData: FormData) => {
        const { orgId, contractId, properties, replace } = formData;

        // Link directly when the user has permission
        if (hasPermission(null, Permission.AllOrganizations)) {
            if (isNew) {
                linkOrgToBuilding(orgId!, contractId, properties, replace);
            }
            else {
                updateBuildingOrg({
                    ...buildingOrgs![orgId!],
                    contractId,
                    properties,
                    replace,
                });
            }
        }
        else {
            // Otherwise, generate a link code for the Building representative to send to the Org
            generateBuildingLinkCode(formData);
        }
    }, [ isNew, buildingOrgs, hasPermission, linkOrgToBuilding, updateBuildingOrg, generateBuildingLinkCode ]);

    // Update the form when a different org comes from the parent (e.g. different edit button clicked; add button clicked)
    useEffect(() => {
        setOrgId(editOrgId);
    }, [ editOrgId, setOrgId ]);

    // Don't show the dialog until the data has loaded
    if (!buildingOrgs) {
        return null;
    }
    if (!isNew && !org) {
        return null;
    }

    return (
        <Dialog
            id="building-org-dialog"
            visible={ visible }
            title={ isNew ? undefined : org.displayName }
            titleKey={ isNew ? "BUILDING.ORGS.ADD_ORG_TITLE" : undefined }
        >
            <BuildingOrgForm
                isNew={ isNew }
                buildingOrgs={ buildingOrgs }
                org={ org }
                contracts={ contracts }
                contractIds={ contractIds }
                hasEcs={ hasEcs }
                busy={ isBusyContracts || state.busy }
                onOrgChanged={ setOrgId }
                onConfigure={ handleConfigure }
                onSave={ handleSave }
                onCancel={ onClose }
            />
        </Dialog>
    );
};
